import Flag from "./Flag";

/**
 * Renders the per-country compensation block as a stacked list of
 * rows in the format `🇬🇧 UK · £100`. One row per country , works
 * for single-country (one row), multi-country (N rows), and falls
 * back to the legacy free-text string if no structured data exists.
 *
 * `rows` and `fallback` come from `selectCompensation(study, ...)`.
 *
 * `variant`:
 *   - `card`        small, used on study cards (compact, no border)
 *   - `card-light`  same shape, light surface
 *   - `panel`       larger, framed lime panel used in modals + detail
 *                   page when we want a self-contained block
 */
export default function CompensationStack({
  rows,
  fallback,
  variant = "card",
  className = "",
  testId,
}) {
  if (!rows || rows.length === 0) {
    if (!fallback) return null;
    return (
      <div className={className} data-testid={testId}>
        <span
          className={
            variant.startsWith("card")
              ? "block text-base font-semibold"
              : "block text-lg font-bold"
          }
        >
          {fallback}
        </span>
      </div>
    );
  }

  const isPanel = variant === "panel" || variant === "panel-stacked";
  const panelStacked = variant === "panel-stacked";
  const isLight = variant === "card-light";

  if (isPanel) {
    return (
      <ul
        className={`grid gap-2 ${panelStacked ? "grid-cols-1" : "grid-cols-1 sm:grid-cols-2"} ${className}`}
        data-testid={testId}
      >
        {rows.map((r) => (
          <li
            key={r.name}
            className="flex items-center justify-between rounded-lg bg-white border border-[var(--light-border)] px-3 py-2.5"
            data-testid={`compensation-row-${r.code || r.name}`}
          >
            <span className="inline-flex items-center gap-2 text-sm text-[var(--charcoal)]">
              <Flag code={r.code} size="md" title={r.name} />
              <span className="font-medium">{r.short}</span>
            </span>
            <span className="text-sm font-bold text-[var(--charcoal)]">
              {r.amount}
            </span>
          </li>
        ))}
      </ul>
    );
  }

  const textClass = isLight
    ? "text-[var(--navy)]"
    : "text-white";
  const subdued = isLight
    ? "text-[var(--text-muted)]"
    : "text-[var(--gray-light)]";

  return (
    <div className={`space-y-0.5 ${className}`} data-testid={testId}>
      {rows.map((r) => (
        <div
          key={r.name}
          className={`flex items-center gap-1.5 text-base font-semibold ${textClass}`}
          data-testid={`compensation-row-${r.code || r.name}`}
        >
          <Flag code={r.code} size="sm" title={r.name} />
          <span className={`uppercase tracking-wider text-xs font-bold ${subdued}`}>
            {r.short}
          </span>
          <span aria-hidden="true" className={subdued}>·</span>
          <span>{r.amount}</span>
        </div>
      ))}
    </div>
  );
}
